import { useMemo, useRef, useState } from 'react';
import {
  IMMUTABLE_OBJECT_ATTRIBUTES,
  OBJECT_ID_KEY,
  RESERVED_OBJECT_ATTRIBUTE_NAMES,
} from '@/services/db/object/entity';
import { isEmptyString } from '@/utils/string';

export type AttributeRow = {
  /** Stable row identity across key/value edits. */
  id: number;
  key: string;
  value: string;
  /** null for rows created in this editing session ("new attribute" rows). */
  originalKey: string | null;
  originalValue: string | null;
  serverError: string | null;
};

export type AttributeRowErrors = { key?: string; value?: string };

type UseObjectAttributesOptions = {
  /** Ordered `{ key: value }` pairs, e.g. from `filteredAttrsWithIdentifiers`. */
  initialAttrs: ReadonlyArray<Record<string, string>>;
  isNew: boolean;
  /**
   * Whether the workspace owns names in this family of attributes: an object's
   * own attributes reserve `object_id` and the generated keys, so a row cannot
   * be renamed to one and `object_id` must carry a value. A relationship's
   * attributes reserve nothing, so those names are ordinary there.
   */
  hasReservedNames?: boolean;
};

const AUTO_GENERATED_ATTRIBUTES: readonly string[] = IMMUTABLE_OBJECT_ATTRIBUTES.filter((key) => key !== OBJECT_ID_KEY);

// The backend's strutil.HasInvalidCharacters equivalent: a replacement
// character means the name arrived with broken encoding.
function hasInvalidCharacters(value: string): boolean {
  return value.includes('�');
}

export function attributeRowErrors(
  row: AttributeRow,
  isNew: boolean,
  duplicateKeys?: ReadonlySet<string>,
  hasReservedNames = true,
): AttributeRowErrors {
  const errors: AttributeRowErrors = {};
  const key = row.key.trim();
  const isKeyChanged = row.key !== row.originalKey;

  if (hasReservedNames && row.key === OBJECT_ID_KEY && isEmptyString(row.value)) {
    errors.value = "Attribute value can't be blank";
  }
  if (isEmptyString(row.key) && !isEmptyString(row.value)) {
    errors.key = "Attribute name can't be blank";
  }
  if (hasInvalidCharacters(row.key)) {
    errors.key = 'Attribute name contains invalid characters';
  }
  if (hasReservedNames && isNew && isKeyChanged && AUTO_GENERATED_ATTRIBUTES.includes(key)) {
    errors.key = `${key} is auto-generated in this workspace`;
  } else if (hasReservedNames && isKeyChanged && RESERVED_OBJECT_ATTRIBUTE_NAMES.includes(key)) {
    errors.key = 'Attribute name is already used';
  }
  if (errors.key == null && duplicateKeys?.has(row.key) === true && !isEmptyString(row.key)) {
    errors.key = 'Attribute name is already used';
  }
  if (row.serverError != null) {
    errors.value = row.serverError;
  }

  return errors;
}

function duplicateKeySet(rows: readonly AttributeRow[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const row of rows) {
    if (row.key === '') continue;
    if (seen.has(row.key)) duplicates.add(row.key);
    seen.add(row.key);
  }
  return duplicates;
}

/**
 * The keys a save must blank (the API's "delete attribute"): every attribute
 * the record started with that no row holds any more, whether its row was
 * removed or renamed. Derived from where the rows stand now rather than kept
 * up step by step, so a name typed through on the way to another (`role` on
 * the way to `roles`) cannot cancel a pending delete, and a key still held by
 * any row — two rows swapping keys, or a key handed on — is never blanked.
 */
function removedKeys(originalRows: readonly AttributeRow[], rows: readonly AttributeRow[]): string[] {
  const held = new Set(rows.map((row) => row.key));
  const originalKeys = originalRows.flatMap((row) => (row.originalKey == null ? [] : [row.originalKey]));
  return [...new Set(originalKeys)].filter((key) => !held.has(key));
}

function rowsFromAttrs(initialAttrs: ReadonlyArray<Record<string, string>>, nextId: () => number): AttributeRow[] {
  return initialAttrs.map((attr) => {
    const [key = ''] = Object.keys(attr);
    return {
      id: nextId(),
      key,
      value: attr[key] ?? '',
      originalKey: key === '' ? null : key,
      originalValue: key === '' ? null : (attr[key] ?? ''),
      serverError: null,
    };
  });
}

/**
 * The object attributes editing model: rows with per-row validation and
 * server errors, plus the `hiddenAttrs` list of removed/renamed keys the
 * save serializes to empty strings (see `removedKeys`).
 */
export function useObjectAttributes({ initialAttrs, isNew, hasReservedNames = true }: UseObjectAttributesOptions) {
  const idRef = useRef(0);
  const nextId = () => ++idRef.current;
  const [originalRows] = useState<AttributeRow[]>(() => rowsFromAttrs(initialAttrs, nextId));
  const [rows, setRows] = useState<AttributeRow[]>(originalRows);
  const hiddenAttrs = useMemo(() => removedKeys(originalRows, rows), [originalRows, rows]);

  const isDirty = useMemo(
    () =>
      JSON.stringify(rows.map((row) => [row.key, row.value])) !==
      JSON.stringify(originalRows.map((row) => [row.key, row.value])),
    [rows, originalRows],
  );
  const duplicateKeys = useMemo(() => duplicateKeySet(rows), [rows]);
  const errorsFor = (row: AttributeRow) => attributeRowErrors(row, isNew, duplicateKeys, hasReservedNames);
  const isInvalid = rows.some((row) => Object.keys(errorsFor(row)).length > 0);

  const create = (key = '', value = '') => {
    setRows((current) => [
      ...current,
      { id: nextId(), key, value, originalKey: null, originalValue: null, serverError: null },
    ]);
  };

  const add = (key: string) => {
    create(key);
  };

  const remove = (row: AttributeRow) => {
    setRows((current) => current.filter((candidate) => candidate.id !== row.id));
  };

  const setValue = (row: AttributeRow, value: string) => {
    setRows((current) =>
      current.map((candidate) => (candidate.id === row.id ? { ...candidate, value, serverError: null } : candidate)),
    );
  };

  const setKey = (row: AttributeRow, key: string) => {
    setRows((current) => current.map((candidate) => (candidate.id === row.id ? { ...candidate, key } : candidate)));
  };

  const setServerError = (key: string, message: string) => {
    setRows((current) =>
      current.map((candidate) => (candidate.key === key ? { ...candidate, serverError: message } : candidate)),
    );
  };

  const reset = () => {
    setRows(originalRows);
  };

  const getModelAttributes = () => {
    const attrs: Record<string, string> = {};
    for (const row of rows) {
      if (row.key !== '') {
        attrs[row.key] = row.value;
      }
    }
    return { attrs, hiddenAttrs };
  };

  return {
    rows,
    hiddenAttrs,
    isDirty,
    isInvalid,
    isNew,
    hasReservedNames,
    errorsFor,
    create,
    add,
    remove,
    setKey,
    setValue,
    setServerError,
    reset,
    getModelAttributes,
  };
}

export type ObjectAttributesEditorState = ReturnType<typeof useObjectAttributes>;
